import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { Tooltip } from 'react-tooltip';
import { BanknotesIcon, BuildingLibraryIcon, LinkIcon, HeartIcon } from '@heroicons/react/24/outline';
import companyApi from '../../../services/companyApi';
import connectApi from '../../../services/connectApi';
import platformApi from '../../../services/platformApi';
import { StatCard, TWButton } from '../../../components/ui';
import { DataTableSkeleton } from '../../../data/columns';
import { formatCurrency } from '../../../utils/baseUtils';
import FinBadge from '../../connect/finance/fin-badge';
import { RoutingLabel } from '../../company/company-detail/company-routing-badge';
import { HEALTH_HELP } from '../../platform-finance/health-strip';
import { BalancePill } from './payouts-tab';
import { summarize } from './overview-summary';
import { ChartCard, TrendChart, TopCompaniesChart } from './overview-charts';

// The Overview tab (spec 2026-09-24, layout A, charts added the same day):
// four tiles, two charts and two short lists, each read on its own so one
// refused read marks one card and never the page. The arithmetic is
// summarize(); this file only reads and draws.
const TREND_MONTHS = 6;

const READS = {
  finance: async () => {
    const from = dayjs().startOf('month').toISOString();
    const to = dayjs().endOf('day').toISOString();
    const res = await platformApi.getFinance(from, to);
    return { rows: platformApi.toPlatformRows(res), totals: platformApi.toPlatformTotals(res) };
  },
  trend: async () => platformApi.toPlatformTrend(await platformApi.getTrend(TREND_MONTHS)),
  balances: async () => (await companyApi.list('?pageNumber=1&pageSize=100&hasbalance=true'))?.data?.data ?? [],
  accounts: async () => connectApi.toConnectAccounts(await connectApi.getConnectAccounts()),
  health: async () => platformApi.toPlatformHealth(await platformApi.getHealth()),
};

const useReads = () => {
  const [state, setState] = useState(() =>
    Object.fromEntries(Object.keys(READS).map((k) => [k, { data: null, loading: true, error: false }])));
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const load = useCallback(async (key) => {
    setState((prev) => ({ ...prev, [key]: { ...prev[key], loading: true, error: false } }));
    try {
      const data = await READS[key]();
      if (mounted.current) setState((prev) => ({ ...prev, [key]: { data, loading: false, error: false } }));
    } catch {
      if (mounted.current) setState((prev) => ({ ...prev, [key]: { data: null, loading: false, error: true } }));
    }
  }, []);

  useEffect(() => { Object.keys(READS).forEach(load); }, [load]);
  return [state, load];
};

// StatCard's own `tone` prop is decorative-only (it never reaches a class),
// so the Fee health tile's rose/emerald/slate has to travel through
// `valueClassName` instead — the one prop StatCard actually renders with.
const HEALTH_TONE_CLASS = {
  rose: 'text-rose-600 dark:text-rose-400',
  emerald: 'text-emerald-600 dark:text-emerald-400',
  slate: '',
};

// The Fee health tile explains itself the way the Revenue strip does, from
// the same words: what its current reading means, and what "fees waived"
// measures.
const healthHelp = (health) => {
  const reading = health.tone === 'rose' ? HEALTH_HELP.critical
    : health.tone === 'emerald' ? HEALTH_HELP.healthy
    : HEALTH_HELP.unreadable;
  return `${reading} ${HEALTH_HELP.feesWaived}`;
};

// A failed tile is drawn without onClick (StatCard then renders a div, not
// a button), so Retry is the tile's only interactive control — a button
// can never nest inside a button. `label` names what failed (the tile's or
// list's own title) so the button's accessible name is "Retry Fee health",
// never a bare, ambiguous "Retry" repeated across the page.
const Failed = ({ onRetry, label }) => (
  <span className="inline-flex items-center gap-2 text-sm text-rose-600 dark:text-rose-400">
    Couldn't load
    <TWButton size="sm" label="Retry" aria-label={`Retry ${label}`} onClick={onRetry} />
  </span>
);

const ListCard = ({ title, seeAllTo, empty, loading, error, onRetry, children, count }) => (
  <section aria-label={title} className="rounded-md border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800/70">
    <div className="mb-3 flex items-center justify-between">
      <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">{title}</h3>
      {seeAllTo ? <Link to={seeAllTo} className="text-xs font-semibold text-blue-600 hover:text-blue-700">See all</Link> : null}
    </div>
    {loading ? <DataTableSkeleton rows={3} headers={[{ label: '', w: 'w-40' }, { label: '', w: 'w-20', align: 'right' }]} />
      : error ? <Failed onRetry={onRetry} label={title} />
      : count === 0 ? <p className="text-sm text-slate-500 dark:text-slate-400">{empty}</p>
      : <ul className="divide-y divide-slate-100 dark:divide-slate-700">{children}</ul>}
  </section>
);

const OverviewTab = () => {
  const navigate = useNavigate();
  const [reads, load] = useReads();
  const s = summarize({
    totals: reads.finance.data?.totals, rows: reads.finance.data?.rows,
    balances: reads.balances.data, accounts: reads.accounts.data, health: reads.health.data,
  });
  const go = (search) => () => navigate(`/dashboard/finance${search}`);
  const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
  const monthName = dayjs().format('MMMM');
  const trend = Array.isArray(reads.trend.data) ? reads.trend.data : [];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard icon={BanknotesIcon} label="Revenue this month" tone="blue" loading={reads.finance.loading} onClick={reads.finance.error ? undefined : go('?tab=revenue')}
          value={reads.finance.error ? <Failed onRetry={() => load('finance')} label="Revenue this month" /> : formatCurrency(s.revenue.net)}
          hint={reads.finance.error ? null : `on ${formatCurrency(s.revenue.gross)} gross`} />
        <StatCard icon={BuildingLibraryIcon} label="Owed from Mercury" tone="amber" loading={reads.balances.loading} onClick={reads.balances.error ? undefined : go('?tab=payouts')}
          value={reads.balances.error ? <Failed onRetry={() => load('balances')} label="Owed from Mercury" /> : formatCurrency(s.owed.total)}
          hint={reads.balances.error ? null : plural(s.owed.companies, 'company to pay', 'companies to pay')} />
        <StatCard icon={LinkIcon} label="Connect accounts" tone="slate" loading={reads.accounts.loading} onClick={reads.accounts.error ? undefined : go('?tab=connect')}
          value={reads.accounts.error ? <Failed onRetry={() => load('accounts')} label="Connect accounts" /> : String(s.connect.total)}
          hint={reads.accounts.error ? null : `${s.connect.direct} Direct · ${s.connect.notStarted} not started · ${s.connect.attention} ${s.connect.attention === 1 ? 'needs' : 'need'} attention`} />
        <StatCard icon={HeartIcon} label="Fee health" tone={s.health.tone} loading={reads.health.loading} onClick={reads.health.error ? undefined : go('?tab=revenue&issues=1')}
          value={reads.health.error ? <Failed onRetry={() => load('health')} label="Fee health" /> : s.health.label}
          valueClassName={reads.health.error ? undefined : HEALTH_TONE_CLASS[s.health.tone] || ''}
          tooltip={reads.health.error ? undefined : healthHelp(s.health)}
          hint={reads.health.error ? null : `${formatCurrency(s.health.feesWaived)} fees waived`} />
      </div>
      <Tooltip id="stat-card-tooltip" className="max-w-xs text-xs" />

      <ChartCard title={`Last ${TREND_MONTHS} months`} hint="Gross and our net, by calendar month."
        loading={reads.trend.loading} error={reads.trend.error} onRetry={() => load('trend')}
        empty={trend.length === 0 ? 'No months to show yet.' : null}>
        <TrendChart points={trend} />
      </ChartCard>

      <ChartCard title={`Top companies in ${monthName}`} hint="This month's biggest companies by gross, with our net beside each."
        loading={reads.finance.loading} error={reads.finance.error} onRetry={() => load('finance')}
        empty={s.charts.top.length === 0 ? 'No revenue this month yet.' : null}>
        <TopCompaniesChart top={s.charts.top} />
      </ChartCard>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ListCard title="To pay from Mercury" seeAllTo="/dashboard/finance?tab=payouts" empty="Nothing to pay. Every legacy company is settled."
          loading={reads.balances.loading} error={reads.balances.error} onRetry={() => load('balances')} count={s.toPay.length}>
          {s.toPay.map((r) => (
            <li key={r.id} className="flex items-center justify-between py-2 text-sm">
              <span className="truncate font-semibold text-slate-900 dark:text-slate-100">{r.name}</span>
              <BalancePill balance={r.balance} />
            </li>
          ))}
        </ListCard>
        <ListCard title="Connect needs attention" seeAllTo="/dashboard/finance?tab=connect" empty="Every Stripe account is complete."
          loading={reads.accounts.loading} error={reads.accounts.error} onRetry={() => load('accounts')} count={s.attention.length}>
          {s.attention.map((r) => (
            <li key={r.companyId} className="flex items-center justify-between gap-3 py-2 text-sm">
              <Link to={`/dashboard/companies/${r.companyId}?tab=connect`} className="truncate font-semibold text-slate-900 hover:text-blue-600 dark:text-slate-100">{r.companyName}</Link>
              <span className="flex shrink-0 items-center gap-2">
                <RoutingLabel model={r.paymentModel} />
                <FinBadge tone="pending" label={`${r.outstanding} ${r.outstanding === 1 ? 'item' : 'items'}`} />
              </span>
            </li>
          ))}
        </ListCard>
      </div>
    </div>
  );
};

export default OverviewTab;
